import { useEffect, useRef, useState } from 'react';
import { encodeWav } from './audio.ts';
import { diagnostic } from './diagnostics.ts';

type RecorderStatus =
  'idle' | 'requesting' | 'listening' | 'recording' | 'transcribing';

// Wait long enough to keep natural pauses inside one turn, without adding more
// than a second of silence to every response.
const quietSecondsToSubmit = 0.8;
const maximumUtteranceSeconds = 30;
const preRollSeconds = 0.4;
const speechQualificationSeconds = 0.24;
const candidateGapSeconds = 0.09;
const minimumSpeechThreshold = 0.015;

export function useRecorder({
  onSpeechStart,
  onComplete,
}: {
  onSpeechStart: () => void;
  onComplete: (wav: Blob, signal: AbortSignal) => Promise<void>;
}) {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');
  const [level, setLevel] = useState(0);
  const generation = useRef(0);
  const turnGeneration = useRef(0);
  const transcription = useRef<AbortController | undefined>(undefined);
  const refs = useRef<{
    context?: AudioContext;
    stream?: MediaStream;
    node?: AudioWorkletNode;
    chunks: Float32Array[];
    active: boolean;
    pending: boolean;
    speech: boolean;
    candidateSeconds: number;
    candidateGap: number;
    quiet: number;
    noiseFloor: number;
    utteranceSeconds: number;
    utteranceTooLong: boolean;
    rate: number;
  }>({
    chunks: [],
    active: false,
    pending: false,
    speech: false,
    candidateSeconds: 0,
    candidateGap: 0,
    quiet: 0,
    noiseFloor: 0.004,
    utteranceSeconds: 0,
    utteranceTooLong: false,
    rate: 48000,
  });
  const callbacks = useRef({ onSpeechStart, onComplete });
  callbacks.current = { onSpeechStart, onComplete };

  function clearUtterance(r = refs.current) {
    r.chunks = [];
    r.speech = false;
    r.candidateSeconds = 0;
    r.candidateGap = 0;
    r.quiet = 0;
    r.utteranceSeconds = 0;
    r.utteranceTooLong = false;
  }

  function releaseCapture() {
    const r = refs.current;
    r.active = false;
    r.pending = false;
    clearUtterance(r);
    r.stream?.getTracks().forEach((track) => track.stop());
    r.node?.disconnect();
    if (r.context && r.context.state !== 'closed') void r.context.close();
    r.context = undefined;
    r.stream = undefined;
    r.node = undefined;
    setLevel(0);
  }

  function abortTurn() {
    transcription.current?.abort();
    transcription.current = undefined;
  }

  function submitUtterance() {
    const r = refs.current;
    if (!r.active || !r.speech || !r.chunks.length) return;
    const chunks = r.chunks;
    const rate = r.rate;
    clearUtterance(r);
    const controller = new AbortController();
    const completedTurn = ++turnGeneration.current;
    transcription.current?.abort();
    transcription.current = controller;
    setStatus('transcribing');
    void callbacks.current
      .onComplete(encodeWav(chunks, rate), controller.signal)
      .catch((err) => {
        if (
          !controller.signal.aborted &&
          completedTurn === turnGeneration.current
        )
          setError(
            err instanceof Error
              ? err.message
              : 'Transcription failed. You can type instead.',
          );
      })
      .finally(() => {
        if (transcription.current === controller)
          transcription.current = undefined;
        if (completedTurn === turnGeneration.current && refs.current.active)
          setStatus('listening');
      });
  }

  function startSpeech(r: typeof refs.current) {
    if (r.speech) return;
    // New speech wins over stale STT/LLM/TTS while capture remains open.
    turnGeneration.current++;
    abortTurn();
    r.speech = true;
    r.quiet = 0;
    r.utteranceSeconds = 0;
    r.utteranceTooLong = false;
    diagnostic('voice-qualification', r.candidateSeconds * 1000, 'ok');
    setError('');
    setStatus('recording');
    callbacks.current.onSpeechStart();
  }

  function onAudioFrame(r: typeof refs.current, data: Float32Array) {
    const rms = Math.sqrt(
      data.reduce((sum, sample) => sum + sample * sample, 0) / data.length,
    );
    setLevel(Math.min(1, rms * 14));
    const seconds = data.length / r.rate;
    const speechThreshold = Math.min(
      0.032,
      Math.max(minimumSpeechThreshold, r.noiseFloor * 2.8),
    );
    // Once a continuous utterance exceeds the server-safe recording bound we
    // keep detecting silence, but retain no tail that could be sent alone.
    if (!r.speech || !r.utteranceTooLong) r.chunks.push(data);
    if (rms > speechThreshold) {
      r.quiet = 0;
      if (!r.speech) {
        r.candidateSeconds += seconds;
        r.candidateGap = 0;
      }
    } else {
      if (r.speech) {
        r.quiet += seconds;
      } else {
        // Learn the room level slowly while idle. The cap prevents a loud room
        // from making the gate impossible to open.
        r.noiseFloor = Math.min(0.011, r.noiseFloor * 0.98 + rms * 0.02);
        if (r.candidateSeconds > 0) {
          r.candidateGap += seconds;
          if (r.candidateGap > candidateGapSeconds) {
            diagnostic(
              'voice-candidate-rejected',
              r.candidateSeconds * 1000,
              'empty',
            );
            r.candidateSeconds = 0;
            r.candidateGap = 0;
          }
        }
      }
    }
    if (!r.speech && r.candidateSeconds >= speechQualificationSeconds)
      startSpeech(r);
    if (!r.speech) {
      while (r.chunks.length && r.chunks.length * seconds > preRollSeconds)
        r.chunks.shift();
      return;
    }
    r.utteranceSeconds += seconds;
    if (r.utteranceSeconds > maximumUtteranceSeconds) r.utteranceTooLong = true;
    if (r.quiet >= quietSecondsToSubmit) {
      if (r.utteranceTooLong) {
        diagnostic(
          'voice-utterance-rejected',
          r.utteranceSeconds * 1000,
          'error',
        );
        clearUtterance(r);
        setError(
          'That was longer than 30 seconds. Please try a shorter question.',
        );
        setStatus('listening');
      } else {
        diagnostic('voice-endpoint', r.quiet * 1000, 'ok');
        submitUtterance();
      }
    }
  }

  async function stop(_submit?: boolean) {
    // `stop` is the explicit off action. The optional legacy argument preserves callers.
    generation.current++;
    turnGeneration.current++;
    abortTurn();
    releaseCapture();
    setEnabled(false);
    setStatus('idle');
  }

  function cancelTurn() {
    turnGeneration.current++;
    abortTurn();
    const r = refs.current;
    clearUtterance(r);
    if (r.active) setStatus('listening');
  }

  async function start() {
    const r = refs.current;
    if (r.active || r.pending) return;
    setError('');
    setEnabled(true);
    setStatus('requesting');
    r.pending = true;
    const requestedGeneration = ++generation.current;
    let ownedStream: MediaStream | undefined;
    let ownedContext: AudioContext | undefined;
    const releaseOwned = () => {
      ownedStream?.getTracks().forEach((track) => track.stop());
      if (ownedContext && ownedContext.state !== 'closed')
        void ownedContext.close();
    };
    try {
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error('This browser cannot record here. Use typed input.');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      ownedStream = stream;
      if (requestedGeneration !== generation.current) {
        releaseOwned();
        return;
      }
      const context = new AudioContext();
      ownedContext = context;
      // Keep ownership visible during worklet setup so an explicit off action
      // immediately releases a granted stream rather than waiting for setup.
      r.stream = stream;
      r.context = context;
      await context.audioWorklet.addModule('/recorder-worklet.js');
      await context.resume();
      if (requestedGeneration !== generation.current) {
        releaseOwned();
        return;
      }
      const node = new AudioWorkletNode(context, 'enactry-recorder');
      const mute = context.createGain();
      mute.gain.value = 0;
      context.createMediaStreamSource(stream).connect(node);
      node.connect(mute).connect(context.destination);
      Object.assign(r, {
        context,
        stream,
        node,
        chunks: [],
        active: true,
        pending: false,
        speech: false,
        candidateSeconds: 0,
        candidateGap: 0,
        quiet: 0,
        noiseFloor: 0.004,
        utteranceSeconds: 0,
        utteranceTooLong: false,
        rate: context.sampleRate,
      });
      node.port.onmessage = ({ data }: MessageEvent<Float32Array>) => {
        if (!r.active || requestedGeneration !== generation.current) return;
        onAudioFrame(r, data);
      };
      setStatus('listening');
    } catch (err) {
      releaseOwned();
      if (requestedGeneration !== generation.current) return;
      releaseCapture();
      setEnabled(false);
      setStatus('idle');
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone permission was not granted. Typed input still works.'
          : err instanceof Error
            ? err.message
            : 'Microphone unavailable. You can type instead.',
      );
    }
  }

  async function toggle() {
    if (refs.current.active || refs.current.pending) await stop();
    else await start();
  }

  useEffect(() => {
    const stopForExit = () => void stop();
    window.addEventListener('pagehide', stopForExit);
    return () => {
      window.removeEventListener('pagehide', stopForExit);
      generation.current++;
      turnGeneration.current++;
      abortTurn();
      releaseCapture();
    };
  }, []);

  return {
    enabled,
    phase: enabled ? status : 'off',
    status,
    error,
    level,
    toggle,
    start,
    stop,
    cancelTurn,
    cancel: () => stop(),
  };
}
